// scheduling/components/pool/SendJobSearchModal.jsx
// "Send a job to a pool" from the Job Pools page: find the job, then hand it to the
// usual Send to Pool modal (Trello #407, Pool Tweaks).
//
// Exists for admin support staff. They fed PE's old "Preparer Pool" account (61% of
// what reached it Jun-Oct 2026) but don't lead the jobs they send, so My Jobs — the
// only other Send to Pool entry point — never lists them. The server returns open,
// poolable jobs that aren't already waiting; `send` re-checks every rule regardless.

import { useEffect, useState } from 'react';
import { searchPoolJobs } from '../../services/poolApi';
import { difficultyBand, difficultyLabel } from '../../utils/poolEligibility';

const fmtHours = (h) => (Math.round((Number(h) || 0) * 100) / 100).toString();
function fmtDate(d) {
  if (!d) return null;
  const s = typeof d === 'string' ? d.slice(0, 10) + 'T12:00:00' : d;
  return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function SendJobSearchModal({ onPick, onClose }) {
  const [q, setQ] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounced so typing a client name doesn't fire a query per keystroke. The `alive`
  // flag drops a slow earlier answer that lands after a newer one.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setJobs([]); setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      searchPoolJobs(term)
        .then(r => { if (alive) { setJobs(r.jobs || []); setError(null); } })
        .catch(e => { if (alive) setError(e.message); })
        .finally(() => { if (alive) setLoading(false); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  return (
    <div className="sched-mgr-modal-backdrop" onClick={onClose}>
      <div className="sched-mgr-modal" onClick={e => e.stopPropagation()}>
        <div className="sched-mgr-modal-header">
          <h3>Send a job to a pool</h3>
          <button className="sched-mgr-sidebar-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="sched-modal-picklist-note">
          Search open jobs that can be pooled, by client name or code, or by job code.
          Jobs already waiting in a pool aren&rsquo;t listed.
        </div>

        <input
          className="sched-modal-job-search"
          placeholder="Client name, client code or job code…"
          value={q}
          onChange={e => setQ(e.target.value)}
          autoFocus
        />

        {error && <div className="sched-pool-error">{error}</div>}

        {q.trim().length < 2 ? (
          <div className="sched-modal-picklist-empty">Type at least two characters.</div>
        ) : loading ? (
          <div className="sched-modal-picklist-empty">Searching…</div>
        ) : jobs.length === 0 ? (
          <div className="sched-modal-picklist-empty">No open poolable jobs match.</div>
        ) : (
          <div className="sched-pool-assign-list">
            {jobs.map(j => (
              <button key={j.JobId} type="button" className="sched-pool-assign-row" onClick={() => onPick(j)}>
                <span className="sched-pool-assign-info">
                  <span className="sched-pool-assign-name">{j.ClientName} · {j.Name}</span>
                  <span className="sched-pool-assign-sub">
                    {j.ClientCode} · {j.ServiceDesc}
                    {j.BudgetHours ? ` · ${fmtHours(j.BudgetHours)}h budget` : ''}
                    {fmtDate(j.Deadline || j.DueDate) ? ` · due ${fmtDate(j.Deadline || j.DueDate)}` : ''}
                    {difficultyBand(j.Complexity) ? ` · ${difficultyLabel(j.Complexity)}` : ''}
                    {j.ManagerName ? ` · Mgr ${j.ManagerName}` : ''}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}

        {jobs.length === 25 && (
          <div className="sched-pool-footnote">Showing the first 25 — type more to narrow it down.</div>
        )}
      </div>
    </div>
  );
}
